import { Link } from 'react-router-dom';
import { Heart, Trash2, MapPin, Building2 } from 'lucide-react';
import { useFavorites } from '@/contexts/FavoritesContext';
import { Button } from '@/components/ui/button';

const Favorites = () => {
  const { favorites, removeFavorite, favoritesCount } = useFavorites();

  if (favoritesCount === 0) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
              <Heart className="w-10 h-10 text-muted-foreground" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-3">
              No favorites yet
            </h1>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              Start exploring businesses and save your favorites by clicking the heart icon.
            </p>
            <Link to="/directory">
              <Button className="bg-primary hover:bg-primary/90" data-testid="browse-directory-btn">
                Browse Directory
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Heart className="w-6 h-6 text-red-500 fill-red-500" />
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              Your Favorites
            </h1>
          </div>
          <p className="text-sm sm:text-base text-muted-foreground">
            {favoritesCount} saved business{favoritesCount !== 1 ? 'es' : ''}
          </p>
        </div>

        {/* Favorites List */}
        <div className="space-y-4" data-testid="favorites-list">
          {favorites.map((business) => (
            <div 
              key={business.id}
              className="bg-card rounded-xl border border-border p-4 flex items-center justify-between gap-4 group hover:shadow-md transition-all"
              data-testid={`favorite-item-${business.id}`}
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                    {business.name}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {business.category && (
                      <span className="truncate">{business.category}</span>
                    )}
                    {business.city && (
                      <>
                        <span>•</span>
                        <span className="flex items-center gap-1 truncate">
                          <MapPin className="w-3 h-3" />
                          {business.city}
                        </span>
                      </>
                    )}
                  </div>
                  {business.addedAt && !isNaN(new Date(business.addedAt)) && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Added {new Date(business.addedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link to={`/directory?search=${encodeURIComponent(business.business_name || '')}${business.btc_map ? '&view=btcmap' : ''}`}>
                  <Button variant="outline" size="sm" data-testid={`view-btn-${business.id}`}>
                    View
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFavorite(business.id)}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                  data-testid={`remove-btn-${business.id}`}
                  aria-label="Remove favorite"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <Link to="/directory" className="text-primary hover:underline text-sm font-medium">
            ← Back to Directory
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Favorites;
